import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import {
  Layers, MapPin, Leaf, Scale, Users, HardHat, Radio, FileBarChart2,
  BarChart3, Satellite, FolderOpen, Search, Bell, Share2, Download,
  Maximize2, Box, Map as MapIcon, ChevronLeft, ChevronRight, ChevronDown,
  Upload, Eye, Edit, Trash2, Compass, Ruler, Pencil, Crosshair,
  Activity, Globe2, Mountain, Cloud, X, Plus, Filter, Clock,
  Wifi, Circle as CircleIcon, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { useDocumentosGDB, DocumentoGDB } from "@/hooks/useDocumentosGDB";
import { SubirDocumentoGDBModal } from "@/components/gdb/SubirDocumentoGDBModal";
import { VisualizadorGDB } from "@/components/gdb/VisualizadorGDB";
import { EditarMetadatosModal } from "@/components/gdb/EditarMetadatosModal";
import { cn } from "@/lib/utils";

/* ===================== Tipos ===================== */
type BaseLayer = "dark" | "satellite" | "hybrid" | "topo";
type SectionKey =
  | "capas" | "marcadores" | "ambiental" | "legal" | "social"
  | "sst" | "sensores" | "ica" | "analisis" | "satelite" | "archivos";

interface NavSection {
  key: SectionKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  count?: number;
}

interface LayerToggle {
  id: string;
  name: string;
  group: SectionKey;
  active: boolean;
  color: string;
  features: number;
}

interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  title: string;
  group: SectionKey;
  status: "ok" | "warn" | "alert";
  meta: Record<string, string>;
}

/* ===================== Data mock GIS ===================== */
const NAV: NavSection[] = [
  { key: "capas",       label: "Capas",        icon: Layers,       color: "#06B6D4" },
  { key: "marcadores",  label: "Marcadores",   icon: MapPin,       color: "#FF6B00" },
  { key: "ambiental",   label: "Ambiental",    icon: Leaf,         color: "#22C55E" },
  { key: "legal",       label: "Legal",        icon: Scale,        color: "#A78BFA" },
  { key: "social",      label: "Social",       icon: Users,        color: "#F59E0B" },
  { key: "sst",         label: "SST",          icon: HardHat,      color: "#3B82F6" },
  { key: "sensores",    label: "Sensores",     icon: Radio,        color: "#EC4899" },
  { key: "ica",         label: "ICA",          icon: FileBarChart2,color: "#EAB308" },
  { key: "analisis",    label: "Análisis",     icon: BarChart3,    color: "#14B8A6" },
  { key: "satelite",    label: "Satélite",     icon: Satellite,    color: "#60A5FA" },
  { key: "archivos",    label: "Archivos GIS", icon: FolderOpen,   color: "#94A3B8" },
];

const INITIAL_LAYERS: LayerToggle[] = [
  { id: "l1", name: "Área de influencia",     group: "ambiental", active: true,  color: "#22C55E", features: 12 },
  { id: "l2", name: "Cobertura vegetal",      group: "ambiental", active: true,  color: "#15803D", features: 48 },
  { id: "l3", name: "Cuerpos de agua",        group: "ambiental", active: false, color: "#06B6D4", features: 23 },
  { id: "l4", name: "Predios — actos admin.", group: "legal",     active: true,  color: "#A78BFA", features: 7  },
  { id: "l5", name: "Resguardos / comunidades",group:"social",    active: false, color: "#F59E0B", features: 5  },
  { id: "l6", name: "Estaciones SST",         group: "sst",       active: true,  color: "#3B82F6", features: 9  },
  { id: "l7", name: "Sensores IoT",           group: "sensores",  active: true,  color: "#EC4899", features: 16 },
  { id: "l8", name: "Estaciones ICA",         group: "ica",       active: false, color: "#EAB308", features: 4  },
  { id: "l9", name: "Imagen satelital 2024",  group: "satelite",  active: false, color: "#60A5FA", features: 1  },
];

const POINTS: MapPoint[] = [
  { id: "p1", lat: 4.6533, lng: -74.0836, title: "Estación monitoreo aire #01", group: "ica",      status: "ok",    meta: { PM10: "32 µg/m³", PM25: "14 µg/m³", "O₃": "0.04 ppm" } },
  { id: "p2", lat: 4.7110, lng: -74.0721, title: "Punto vertimiento V-04",       group: "ambiental",status: "warn",  meta: { Caudal: "1.2 L/s", pH: "7.4", DBO: "estable" } },
  { id: "p3", lat: 4.6286, lng: -74.0646, title: "Sensor IoT — ruido",           group: "sensores", status: "ok",    meta: { Nivel: "58 dB", Tendencia: "↘" } },
  { id: "p4", lat: 4.6951, lng: -74.0307, title: "Predio resolución 0431/24",    group: "legal",    status: "alert", meta: { Estado: "Vencido", Responsable: "J. García" } },
  { id: "p5", lat: 4.5981, lng: -74.0758, title: "Comunidad El Mirador",         group: "social",   status: "ok",    meta: { Hogares: "84", Reuniones: "12/12" } },
  { id: "p6", lat: 4.7569, lng: -74.0359, title: "Frente obra — Brigada SST",    group: "sst",      status: "warn",  meta: { Personal: "23", "EPP": "OK" } },
];

const TILE_LAYERS: Record<BaseLayer, { url: string; attribution: string; subdomains?: string }> = {
  dark:      { url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attribution: "© OSM, © CARTO", subdomains: "abcd" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles © Esri" },
  hybrid:    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles © Esri" },
  topo:      { url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", attribution: "© OpenTopoMap", subdomains: "abc" },
};

/* ===================== Util ===================== */
const statusColor = (s: MapPoint["status"]) =>
  s === "ok" ? "#22C55E" : s === "warn" ? "#F59E0B" : "#EF4444";

const fmtBytes = (b: number) => {
  if (!b) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/* ===================== Componente ===================== */
export default function GestorGDB() {
  const navigate = useNavigate();
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const hybridLabelRef = useRef<L.TileLayer | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightOpen, setRightOpen] = useState(true);
  const [bottomOpen, setBottomOpen] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionKey>("capas");
  const [layers, setLayers] = useState<LayerToggle[]>(INITIAL_LAYERS);
  const [baseLayer, setBaseLayer] = useState<BaseLayer>("dark");
  const [view3D, setView3D] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number; zoom: number }>({ lat: 4.65, lng: -74.08, zoom: 11 });
  const [selected, setSelected] = useState<MapPoint | null>(POINTS[0]);
  const [search, setSearch] = useState("");
  const [modalSubir, setModalSubir] = useState(false);
  const [modalVisor, setModalVisor] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [docSel, setDocSel] = useState<DocumentoGDB | null>(null);

  const { documentos, deleteDocumento } = useDocumentosGDB();

  /* ===== Map init ===== */
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, {
      center: [coords.lat, coords.lng],
      zoom: coords.zoom,
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
    });
    mapRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.control.attribution({ position: "bottomleft", prefix: false }).addTo(map);

    map.on("mousemove", (e) => setCoords({ lat: e.latlng.lat, lng: e.latlng.lng, zoom: map.getZoom() }));
    map.on("zoomend moveend", () => setCoords((c) => ({ ...c, zoom: map.getZoom() })));

    const syncMapSize = () => window.requestAnimationFrame(() => map.invalidateSize(false));
    const resizeObserver = new ResizeObserver(syncMapSize);
    resizeObserver.observe(mapContainerRef.current);
    window.addEventListener("resize", syncMapSize);
    syncMapSize();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncMapSize);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Base layer swap ===== */
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (tileRef.current) { tileRef.current.remove(); tileRef.current = null; }
    if (hybridLabelRef.current) { hybridLabelRef.current.remove(); hybridLabelRef.current = null; }
    const cfg = TILE_LAYERS[baseLayer];
    const tileOptions: L.TileLayerOptions = {
      attribution: cfg.attribution,
      maxZoom: 19,
    };

    if (cfg.subdomains) {
      tileOptions.subdomains = cfg.subdomains;
    }

    tileRef.current = L.tileLayer(cfg.url, tileOptions).addTo(map);
    if (baseLayer === "hybrid") {
      hybridLabelRef.current = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png",
        { attribution: "© CARTO", subdomains: "abcd", maxZoom: 19 }
      ).addTo(map);
    }

    window.requestAnimationFrame(() => map.invalidateSize(false));
  }, [baseLayer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const frame = window.requestAnimationFrame(() => map.invalidateSize(false));
    return () => window.cancelAnimationFrame(frame);
  }, [sidebarCollapsed, rightOpen, bottomOpen, view3D]);

  /* ===== Render markers from active groups ===== */
  useEffect(() => {
    const map = mapRef.current; const group = markersRef.current; if (!map || !group) return;
    group.clearLayers();
    const activeGroups = new Set(layers.filter(l => l.active).map(l => l.group));
    POINTS.filter(p => activeGroups.has(p.group)).forEach((p) => {
      const color = statusColor(p.status);
      const icon = L.divIcon({
        className: "",
        html: `
          <div style="position:relative;width:28px;height:28px;">
            <div style="position:absolute;inset:0;border-radius:9999px;background:${color}33;animation:gdb-ping 2s ease-out infinite;"></div>
            <div style="position:absolute;inset:6px;border-radius:9999px;background:${color};box-shadow:0 0 12px ${color}cc, 0 0 0 2px #0f172a;"></div>
          </div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      const m = L.marker([p.lat, p.lng], { icon }).addTo(group);
      m.on("click", () => { setSelected(p); setRightOpen(true); });
    });
  }, [layers]);

  const counts = useMemo(() => {
    const c: Record<SectionKey, number> = { capas: layers.length, marcadores: POINTS.length, ambiental: 0, legal: 0, social: 0, sst: 0, sensores: 0, ica: 0, analisis: 4, satelite: 0, archivos: documentos.length };
    layers.forEach(l => { c[l.group] = (c[l.group] || 0) + 1; });
    return c;
  }, [layers, documentos.length]);

  const filteredDocs = useMemo(
    () => documentos.filter(d => d.nombre_documento.toLowerCase().includes(search.toLowerCase())),
    [documentos, search]
  );

  const toggleLayer = (id: string) =>
    setLayers(ls => ls.map(l => (l.id === id ? { ...l, active: !l.active } : l)));

  const flyTo = (p: MapPoint) => {
    mapRef.current?.flyTo([p.lat, p.lng], 14, { duration: 1.1 });
    setSelected(p);
    setRightOpen(true);
  };

  /* ===================== Render ===================== */
  return (
    <TooltipProvider delayDuration={200}>
      <style>{`
        @keyframes gdb-ping { 0%{transform:scale(0.6);opacity:.9} 80%,100%{transform:scale(2.4);opacity:0} }
        .gdb-scroll::-webkit-scrollbar{width:6px}
        .gdb-scroll::-webkit-scrollbar-thumb{background:#334155;border-radius:6px}
        .gdb-glass{background:rgba(15,23,42,0.72);backdrop-filter:blur(16px) saturate(160%);-webkit-backdrop-filter:blur(16px) saturate(160%);border:1px solid rgba(148,163,184,0.12)}
        .gdb-glass-strong{background:rgba(17,24,39,0.88);backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);border:1px solid rgba(148,163,184,0.14)}
        .leaflet-container{background:#0b1220}
        .leaflet-control-zoom a{background:rgba(17,24,39,0.85)!important;color:#e2e8f0!important;border:1px solid rgba(148,163,184,0.15)!important;backdrop-filter:blur(8px)}
        .leaflet-control-zoom a:hover{background:rgba(255,107,0,0.2)!important;color:#FF6B00!important}
        .leaflet-control-attribution{background:rgba(15,23,42,0.6)!important;color:#94a3b8!important;font-size:10px!important;padding:2px 8px!important;backdrop-filter:blur(8px)}
      `}</style>

      <div
        className="relative flex bg-[#0b1220] text-slate-100 overflow-hidden w-full"
        style={{ height: "calc(100vh - 4rem)", fontFamily: "Inter, Manrope, system-ui" }}
      >
        {/* ============ LEFT SIDEBAR ============ */}
        <aside className={cn(
          "relative h-full flex flex-col transition-all duration-300 ease-out gdb-glass-strong border-r border-slate-700/40 z-30",
          sidebarCollapsed ? "w-[68px]" : "w-[280px]"
        )}>
          {/* Brand */}
          <div className="h-14 px-3 flex items-center justify-between border-b border-slate-700/40">
            <button
              onClick={() => navigate("/home")}
              className="flex items-center gap-2 group"
            >
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#FF6B00] to-[#FF8A3D] grid place-items-center shadow-[0_0_18px_rgba(255,107,0,0.45)]">
                <Globe2 className="h-5 w-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div className="leading-tight">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Viabiliza</div>
                  <div className="text-sm font-semibold">GDB · Geo Center</div>
                </div>
              )}
            </button>
            <button
              onClick={() => setSidebarCollapsed(v => !v)}
              className="h-8 w-8 grid place-items-center rounded-md hover:bg-slate-700/40 text-slate-400"
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>

          {/* Nav */}
          <ScrollArea className="flex-1">
            <nav className="px-2 py-3 space-y-0.5">
              {NAV.map((s) => {
                const Icon = s.icon;
                const active = activeSection === s.key;
                return (
                  <Tooltip key={s.key}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setActiveSection(s.key)}
                        className={cn(
                          "group w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-all duration-200 relative overflow-hidden",
                          active ? "bg-slate-700/40 text-white" : "text-slate-300 hover:bg-slate-700/25 hover:text-white"
                        )}
                      >
                        <span
                          className={cn(
                            "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r transition-all",
                            active ? "h-7 opacity-100" : "h-0 opacity-0"
                          )}
                          style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }}
                        />
                        <span
                          className="h-8 w-8 grid place-items-center rounded-md shrink-0 transition-transform group-hover:scale-105"
                          style={{ background: `${s.color}1a`, color: s.color, border: `1px solid ${s.color}33` }}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="flex-1 text-left truncate">{s.label}</span>
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 text-[10px] bg-slate-800/60 border-slate-600/50 text-slate-300"
                            >
                              {counts[s.key] ?? 0}
                            </Badge>
                          </>
                        )}
                      </button>
                    </TooltipTrigger>
                    {sidebarCollapsed && <TooltipContent side="right">{s.label}</TooltipContent>}
                  </Tooltip>
                );
              })}
            </nav>

            {!sidebarCollapsed && (
              <div className="px-3 pb-4 pt-2">
                <div className="rounded-xl p-3 bg-gradient-to-br from-[#FF6B00]/15 to-[#06B6D4]/10 border border-slate-700/50">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                    <Activity className="h-3 w-3" /> Estado del sistema
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="rounded-lg bg-slate-900/60 py-2">
                      <div className="text-lg font-semibold text-emerald-400">{layers.filter(l => l.active).length}</div>
                      <div className="text-[10px] text-slate-400">Capas activas</div>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 py-2">
                      <div className="text-lg font-semibold text-cyan-400">{POINTS.length}</div>
                      <div className="text-[10px] text-slate-400">Features</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>

          {!sidebarCollapsed && (
            <button
              onClick={() => navigate("/home")}
              className="m-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-700/30 flex items-center gap-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Volver a Viabiliza
            </button>
          )}
        </aside>

        {/* ============ CENTER (map + overlays) ============ */}
        <div className="relative flex-1 min-w-0">
          {/* MAP */}
          <div ref={mapContainerRef} className="absolute inset-0" />

          {/* Dark vignette + 3D tilt illusion */}
          <div
            className="pointer-events-none absolute inset-0 transition-all duration-500"
            style={{
              background: view3D
                ? "linear-gradient(180deg, rgba(11,18,32,0.55) 0%, rgba(11,18,32,0) 35%, rgba(11,18,32,0.35) 100%)"
                : "radial-gradient(ellipse at center, transparent 55%, rgba(11,18,32,0.55) 100%)",
              transform: view3D ? "perspective(1400px) rotateX(18deg)" : "none",
              transformOrigin: "center 60%",
            }}
          />

          {/* ============ FLOATING TOPBAR ============ */}
          <div className="absolute top-3 left-3 right-3 z-20 flex items-center gap-2">
            <div className="gdb-glass rounded-xl px-3 h-12 flex items-center gap-2 flex-1 min-w-0 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar coordenada, capa, archivo, estación…"
                className="bg-transparent outline-none border-0 text-sm flex-1 min-w-0 placeholder:text-slate-500 text-slate-100"
              />
              <div className="hidden md:flex items-center gap-1.5 text-[11px] text-slate-400 pl-2 ml-1 border-l border-slate-700/50">
                <Crosshair className="h-3 w-3" />
                <span className="font-mono">{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</span>
                <span className="text-slate-500">· z{coords.zoom}</span>
              </div>
            </div>

            {/* Base layer selector */}
            <div className="gdb-glass rounded-xl h-12 px-1.5 hidden lg:flex items-center gap-1 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              {([
                { k: "dark",      label: "Dark",    icon: MapIcon },
                { k: "satellite", label: "Satélite",icon: Satellite },
                { k: "hybrid",    label: "Híbrido", icon: Globe2 },
                { k: "topo",      label: "Topo",    icon: Mountain },
              ] as const).map(({ k, label, icon: I }) => (
                <button
                  key={k}
                  onClick={() => setBaseLayer(k)}
                  className={cn(
                    "h-9 px-2.5 rounded-lg text-xs flex items-center gap-1.5 transition-all",
                    baseLayer === k
                      ? "bg-[#FF6B00]/15 text-[#FF8A3D] border border-[#FF6B00]/30"
                      : "text-slate-300 hover:bg-slate-700/40 border border-transparent"
                  )}
                >
                  <I className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* 2D / 3D */}
            <div className="gdb-glass rounded-xl h-12 px-1.5 flex items-center gap-1 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              <button
                onClick={() => setView3D(false)}
                className={cn(
                  "h-9 px-3 rounded-lg text-xs flex items-center gap-1.5",
                  !view3D ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" : "text-slate-300 hover:bg-slate-700/40 border border-transparent"
                )}
              >
                2D
              </button>
              <button
                onClick={() => setView3D(true)}
                className={cn(
                  "h-9 px-3 rounded-lg text-xs flex items-center gap-1.5",
                  view3D ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" : "text-slate-300 hover:bg-slate-700/40 border border-transparent"
                )}
              >
                <Box className="h-3.5 w-3.5" /> 3D
              </button>
            </div>

            {/* Actions */}
            <div className="gdb-glass rounded-xl h-12 px-1.5 hidden md:flex items-center gap-0.5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              {[
                { icon: Clock,     label: "Timeline" },
                { icon: Filter,    label: "Filtros" },
                { icon: Download,  label: "Exportar" },
                { icon: Share2,    label: "Compartir" },
                { icon: Bell,      label: "Alertas",   badge: 3 },
              ].map(({ icon: I, label, badge }) => (
                <Tooltip key={label}>
                  <TooltipTrigger asChild>
                    <button className="relative h-9 w-9 grid place-items-center rounded-lg text-slate-300 hover:bg-slate-700/40 hover:text-white">
                      <I className="h-4 w-4" />
                      {badge && (
                        <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-[#FF6B00] text-[9px] font-semibold grid place-items-center text-white">
                          {badge}
                        </span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{label}</TooltipContent>
                </Tooltip>
              ))}
            </div>

            {/* GPS status */}
            <div className="gdb-glass rounded-xl h-12 px-3 hidden xl:flex items-center gap-2 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-[11px] text-slate-300">GPS · Online</span>
            </div>
          </div>

          {/* ============ FLOATING MAP TOOLS (left) ============ */}
          <div className="absolute left-3 top-20 z-20 flex flex-col gap-2">
            <div className="gdb-glass rounded-xl p-1 flex flex-col gap-0.5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              {[
                { icon: Ruler,     label: "Medir" },
                { icon: Pencil,    label: "Dibujar" },
                { icon: Compass,   label: "Geocercas" },
                { icon: Crosshair, label: "Mi ubicación" },
                { icon: Maximize2, label: "Pantalla completa" },
              ].map(({ icon: I, label }) => (
                <Tooltip key={label}>
                  <TooltipTrigger asChild>
                    <button className="h-9 w-9 grid place-items-center rounded-lg text-slate-300 hover:bg-[#FF6B00]/15 hover:text-[#FF8A3D] transition-colors">
                      <I className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{label}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>

          {/* ============ KPI STRIP (top-center under topbar) ============ */}
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 hidden xl:flex gap-2">
            {[
              { label: "Cumplimiento ambiental", value: "94%", color: "#22C55E" },
              { label: "Áreas monitoreadas",     value: "12.4k ha", color: "#06B6D4" },
              { label: "Alertas activas",        value: "3",  color: "#F59E0B" },
              { label: "Sensores en línea",      value: "16/16", color: "#FF6B00" },
            ].map((k) => (
              <div key={k.label} className="gdb-glass rounded-xl px-4 py-2 min-w-[148px] shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{k.label}</div>
                <div className="text-lg font-semibold" style={{ color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* ============ RIGHT CONTEXTUAL PANEL ============ */}
          <aside className={cn(
            "absolute top-3 bottom-3 right-3 z-20 w-[340px] rounded-2xl gdb-glass-strong shadow-[0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-300 ease-out",
            rightOpen ? "translate-x-0 opacity-100" : "translate-x-[110%] opacity-0 pointer-events-none"
          )}>
            <div className="h-12 px-4 flex items-center justify-between border-b border-slate-700/40">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: selected ? statusColor(selected.status) : "#64748b" }} />
                <span className="text-xs uppercase tracking-wider text-slate-400">Detalle del elemento</span>
              </div>
              <button onClick={() => setRightOpen(false)} className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-700/50 text-slate-400">
                <X className="h-4 w-4" />
              </button>
            </div>

            <ScrollArea className="h-[calc(100%-3rem)]">
              {selected ? (
                <div className="p-4 space-y-4">
                  <div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                      {NAV.find(n => n.key === selected.group)?.label}
                    </div>
                    <h3 className="text-base font-semibold text-white mt-1">{selected.title}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge
                        className="border-0 text-[10px]"
                        style={{ background: `${statusColor(selected.status)}25`, color: statusColor(selected.status) }}
                      >
                        {selected.status === "ok" ? "Operativo" : selected.status === "warn" ? "Atención" : "Alerta crítica"}
                      </Badge>
                      <span className="text-[11px] font-mono text-slate-400">
                        {selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}
                      </span>
                    </div>
                  </div>

                  {/* Mini hero */}
                  <div className="h-24 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-40" style={{
                      background: `radial-gradient(circle at 30% 40%, ${statusColor(selected.status)}66, transparent 60%), radial-gradient(circle at 70% 70%, #06B6D466, transparent 60%)`
                    }} />
                    <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between text-[10px] text-slate-300">
                      <span>Vista preliminar</span>
                      <span className="font-mono">EPSG:4326</span>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Metadata</div>
                    {Object.entries(selected.meta).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-md hover:bg-slate-700/30">
                        <span className="text-slate-400">{k}</span>
                        <span className="text-slate-100 font-medium font-mono">{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* Tabs */}
                  <div className="grid grid-cols-3 gap-1.5">
                    {["Evidencias", "Cumplimiento", "Historial"].map((t) => (
                      <button key={t} className="text-[11px] py-2 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 text-slate-300 hover:text-white">
                        {t}
                      </button>
                    ))}
                  </div>

                  {/* Trazabilidad */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Trazabilidad</div>
                    <ol className="relative ml-2 border-l border-slate-700/60 space-y-3 pl-4">
                      {[
                        { t: "Hoy 09:12", d: "Lectura sincronizada", c: "#22C55E" },
                        { t: "Ayer 18:40", d: "Evidencia cargada", c: "#06B6D4" },
                        { t: "20 may", d: "Inspección en sitio", c: "#FF6B00" },
                      ].map((e, i) => (
                        <li key={i} className="relative">
                          <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-[#0f172a]" style={{ background: e.c }} />
                          <div className="text-[11px] text-slate-400">{e.t}</div>
                          <div className="text-xs text-slate-200">{e.d}</div>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <Button className="w-full bg-gradient-to-r from-[#FF6B00] to-[#FF8A3D] hover:opacity-90 text-white">
                    Abrir ficha completa
                  </Button>
                </div>
              ) : (
                <div className="p-6 text-center text-slate-400 text-sm">
                  Selecciona un elemento en el mapa para ver su detalle.
                </div>
              )}
            </ScrollArea>
          </aside>

          {/* Right panel reopen handle */}
          {!rightOpen && (
            <button
              onClick={() => setRightOpen(true)}
              className="absolute right-3 top-20 z-20 gdb-glass rounded-xl h-12 w-10 grid place-items-center text-slate-300 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          {/* ============ BOTTOM PANEL ============ */}
          <div className={cn(
            "absolute left-3 right-[360px] bottom-3 z-10 rounded-2xl gdb-glass-strong shadow-[0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-300",
            bottomOpen ? "h-[260px]" : "h-12"
          )}>
            <div className="h-12 px-4 flex items-center justify-between border-b border-slate-700/40">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setBottomOpen(v => !v)}
                  className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-700/40 text-slate-400"
                >
                  <ChevronDown className={cn("h-4 w-4 transition-transform", bottomOpen ? "" : "rotate-180")} />
                </button>
                <span className="text-xs uppercase tracking-wider text-slate-400">
                  {activeSection === "archivos" ? "Archivos GIS" : activeSection === "analisis" ? "Análisis territorial" : "Capas & Features"}
                </span>
                <Badge className="bg-slate-800 border border-slate-700/60 text-slate-300 text-[10px]">
                  {activeSection === "archivos" ? filteredDocs.length : layers.length}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {activeSection === "archivos" && (
                  <Button size="sm" onClick={() => setModalSubir(true)} className="h-8 bg-gradient-to-r from-[#FF6B00] to-[#FF8A3D] hover:opacity-90 text-white">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Subir
                  </Button>
                )}
                <span className="hidden md:flex items-center gap-1 text-[10px] text-slate-500">
                  <Wifi className="h-3 w-3 text-emerald-400" /> Realtime sync
                </span>
              </div>
            </div>

            {bottomOpen && (
              <div className="h-[calc(100%-3rem)] overflow-hidden">
                {activeSection === "archivos" ? (
                  <ScrollArea className="h-full">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-900/80 backdrop-blur z-10">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="py-2 px-4 font-medium">Documento</th>
                          <th className="py-2 px-4 font-medium">Categoría</th>
                          <th className="py-2 px-4 font-medium">Tipo</th>
                          <th className="py-2 px-4 font-medium">Tamaño</th>
                          <th className="py-2 px-4 font-medium text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocs.length === 0 && (
                          <tr><td colSpan={5} className="text-center py-10 text-slate-500">Sin documentos cargados</td></tr>
                        )}
                        {filteredDocs.map((d) => (
                          <tr key={d.id} className="border-t border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                            <td className="py-2.5 px-4">
                              <div className="flex items-center gap-2">
                                <div className="h-7 w-7 rounded-md bg-cyan-500/10 text-cyan-300 grid place-items-center">
                                  <FolderOpen className="h-3.5 w-3.5" />
                                </div>
                                <div className="text-slate-200">{d.nombre_documento}</div>
                              </div>
                            </td>
                            <td className="py-2.5 px-4 text-slate-400">{d.categoria}</td>
                            <td className="py-2.5 px-4 font-mono text-slate-400">{d.tipo_archivo}</td>
                            <td className="py-2.5 px-4 font-mono text-slate-400">{fmtBytes(d.tamano_archivo)}</td>
                            <td className="py-2.5 px-4">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => { setDocSel(d); setModalVisor(true); }} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-700/50 text-slate-300"><Eye className="h-3.5 w-3.5" /></button>
                                <button onClick={() => { setDocSel(d); setModalEditar(true); }} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-700/50 text-slate-300"><Edit className="h-3.5 w-3.5" /></button>
                                <a href={d.url_archivo} target="_blank" rel="noreferrer" className="h-7 w-7 grid place-items-center rounded hover:bg-slate-700/50 text-slate-300"><Download className="h-3.5 w-3.5" /></a>
                                <button onClick={() => { setDocSel(d); setModalEliminar(true); }} className="h-7 w-7 grid place-items-center rounded hover:bg-red-500/15 hover:text-red-300 text-slate-300"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                ) : activeSection === "analisis" ? (
                  <div className="p-4 grid grid-cols-4 gap-3 h-full">
                    {[
                      { t: "Cobertura vegetal", v: "8 240 ha", trend: "+2.4%", c: "#22C55E" },
                      { t: "Áreas intervenidas", v: "1 120 ha", trend: "-0.8%", c: "#FF6B00" },
                      { t: "Cuerpos de agua",   v: "42 puntos",trend: "+1",    c: "#06B6D4" },
                      { t: "Alertas geográficas", v: "3 zonas", trend: "estable", c: "#F59E0B" },
                    ].map((k) => (
                      <div key={k.t} className="rounded-xl bg-slate-800/40 border border-slate-700/50 p-4 flex flex-col justify-between">
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">{k.t}</div>
                        <div className="text-2xl font-semibold" style={{ color: k.c }}>{k.v}</div>
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] text-slate-400">{k.trend}</div>
                          <div className="h-6 flex items-end gap-0.5">
                            {[3,5,4,6,8,5,9,7,10,8].map((h, i) => (
                              <span key={i} className="w-1 rounded-sm" style={{ height: `${h*2}px`, background: k.c, opacity: 0.4 + i*0.06 }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ScrollArea className="h-full">
                    <div className="p-3 grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {layers.map((l) => {
                        const nav = NAV.find(n => n.key === l.group);
                        return (
                          <div
                            key={l.id}
                            className={cn(
                              "group rounded-xl p-3 border transition-all cursor-pointer",
                              l.active
                                ? "bg-slate-800/50 border-slate-600/60"
                                : "bg-slate-900/40 border-slate-800/60 hover:border-slate-700"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="h-7 w-7 rounded-md grid place-items-center shrink-0" style={{ background: `${l.color}1a`, color: l.color, border: `1px solid ${l.color}33` }}>
                                  <Layers className="h-3.5 w-3.5" />
                                </span>
                                <div className="min-w-0">
                                  <div className="text-xs text-slate-100 truncate">{l.name}</div>
                                  <div className="text-[10px] text-slate-500">{nav?.label} · {l.features} features</div>
                                </div>
                              </div>
                              <Switch checked={l.active} onCheckedChange={() => toggleLayer(l.id)} />
                            </div>
                            {l.active && (
                              <div className="mt-2 h-1 rounded-full bg-slate-700/60 overflow-hidden">
                                <div
                                  className="h-full transition-all"
                                  style={{ width: `${Math.min(100, l.features * 4)}%`, background: l.color, boxShadow: `0 0 8px ${l.color}` }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button
                        onClick={() => setModalSubir(true)}
                        className="rounded-xl border border-dashed border-slate-700 hover:border-[#FF6B00]/50 hover:bg-[#FF6B00]/5 text-slate-400 hover:text-[#FF8A3D] p-3 flex items-center justify-center gap-2 text-xs"
                      >
                        <Plus className="h-4 w-4" /> Añadir capa / archivo
                      </button>
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>

          {/* Quick jump points (overlay chip) */}
          <div className="absolute bottom-3 right-[360px] mr-3 z-20 hidden xl:flex flex-col gap-1.5">
            {POINTS.slice(0, 4).map((p) => (
              <button
                key={p.id}
                onClick={() => flyTo(p)}
                className="gdb-glass rounded-lg px-3 h-9 flex items-center gap-2 text-[11px] text-slate-300 hover:text-white"
              >
                <CircleIcon className="h-2.5 w-2.5" style={{ color: statusColor(p.status), fill: statusColor(p.status) }} />
                <span className="truncate max-w-[160px]">{p.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============ Modales existentes ============ */}
      <SubirDocumentoGDBModal isOpen={modalSubir} onClose={() => setModalSubir(false)} />
      {docSel && (
        <>
          <VisualizadorGDB
            documento={docSel}
            isOpen={modalVisor}
            onClose={() => { setModalVisor(false); setDocSel(null); }}
          />
          <EditarMetadatosModal
            documento={docSel}
            isOpen={modalEditar}
            onClose={() => { setModalEditar(false); setDocSel(null); }}
          />
        </>
      )}
      <AlertDialog open={modalEliminar} onOpenChange={setModalEliminar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará "{docSel?.nombre_documento}" permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { if (docSel) { await deleteDocumento(docSel); setDocSel(null); setModalEliminar(false); } }}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
